import { useState, useEffect, useCallback } from "react";
import { Box, Flex, IconButton, Button, Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";
import { MdDelete, MdSearch, MdEdit,MdCopyAll } from "react-icons/md";

import Page from "../components/common/Page";
import { ApiCommand } from "../lib/Api";
import useToastNotification from "../lib/hooks/useToastNotification";
import StrongTable from "../components/common/StrongTable";
import StrongPagination from "../components/common/StrongPagination";
import useApi from "../lib/hooks/useApi";
import urlConstants from "../lib/constants/url.constants";
import EditProgramInfoModal from "../components/programInfo/EditProgramInfoModal";
import StrongInput from "../components/common/StrongInput";
import ThumbnailPreview from "../components/common/ThumbnailPreview";
import { useNavigate } from "react-router-dom";
import { Path } from "../lib/constants/path.constants";

const SECTION_COLUMNS = [
  {
    key: "title",
    label: "Title",
  },
  {
    key: "cover",
    label: "Cover",
    render: (value: string) => <ThumbnailPreview imageUrl={value} />,
  },
  {
    key: "thumbnail",
    label: "Thumbnail",
    render: (value: string) => <ThumbnailPreview imageUrl={value} />,
  },
  {
    key: "type",
    label: "Type",
  },
  {
    key: "duration",
    label: "Duration",
  },
  //remaining days calculation
  { key: "weeksRemaining", label: "Weeks Left" },
  { key: "remainingDays", label: "Days Left" },
];

const {
  createSection: createSectionUrl,
  getSections: getSectionsUrl,
  editSection: editSectionUrl,
  deleteSection: deleteSectionUrl,
  duplicateSection: duplicateSectionUrl,
} = urlConstants.programInfo;
const { getEquipmentstitle: getEquipmentsTitleUrl } = urlConstants.exercises;
const { getResourcesTitle: getResourcesTitleUrl } = urlConstants.resources;

const normalizeSectionGenders = (section: Section) => {
  const explicit = (section.genders || [])
    .map((gender) => gender.toLowerCase().trim())
    .filter((gender) => gender === "male" || gender === "female");

  return Array.from(new Set(explicit));
};

const isPrequelProgram = (section: Section) => {
  const title = (section.title || "").toLowerCase();
  return title.includes("prequel") || title.includes("intro to strength training");
};

const sortPrequelFirst = (sections: Section[]) => {
  return [...sections].sort((a, b) => {
    const aPrequel = isPrequelProgram(a);
    const bPrequel = isPrequelProgram(b);
    if (aPrequel === bPrequel) return 0;
    return aPrequel ? -1 : 1;
  });
};

const filterSectionsByGender = (
  sections: Section[],
  gender: "all" | "male" | "female",
) => {
  const filtered =
    gender === "all"
      ? sections
      : sections.filter(
          (section) =>
            isPrequelProgram(section) ||
            normalizeSectionGenders(section).includes(gender),
        );
  return sortPrequelFirst(filtered);
};

const ProgramInfo = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<Section[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [selectedSection, setSelectedSection] = useState<Section>();
  const [selectedGenderTab, setSelectedGenderTab] = useState<'all' | 'male' | 'female'>('all');
  const [equipmentstitle, setEquipmentsTitle] = useState<EquipmentTitle[]>([]);
  const [resourcestitle, setResourcesTitle] = useState<ResourceTitle[]>([]);

  const showToast = useToastNotification();

  const { loading: isFetchUsersLoading, sendRequest: getSections } =
    useApi<GetSectionsResopnse>();
  const { loading: isCreateSectionLoading, sendRequest: createSection } =
    useApi<any>();
  const { loading: isEditSectionLoading, sendRequest: editSection } =
    useApi<any>();
  const { loading: isDeleteSectionLoading, sendRequest: deleteSection } =
    useApi<any>();
  const { loading: isGetEquipmentsTitleLoading, sendRequest: getEquipmentstitle } =
    useApi<EquipmentTitle[]>();
  const { sendRequest: getResourcesTitle } = useApi<ResourceTitle[]>();

  const { loading: isDuplicateSectionLoading, sendRequest:
   duplicateSection } =
    useApi<any>();
  
  const fetchSections = () => {
    const isGenderFilter = selectedGenderTab !== "all";
    getSections({
      callback: (data: GetSectionsResopnse | null, error: string | null) => {
        if (error) {
          showToast({
            title: "Failed",
            description: error,
            status: "error",
          });
          return;
        }
        if (!data) return null;
        console.log('ProgramInfo: fetched sections response:', data);
        setSections(data.sections);
        if (isGenderFilter) {
          const filteredCount = filterSectionsByGender(
            data.sections,
            selectedGenderTab,
          ).length;
          setTotalPages(Math.max(1, Math.ceil(filteredCount / pageSize)));
          setTotalCount(filteredCount);
        } else {
          setTotalPages(data.pagination.totalPages);
          setTotalCount(data.pagination.totalCount);
        }
      },
      command: ApiCommand.GET,
      url: getSectionsUrl,
      options: {
        page: isGenderFilter ? 1 : currentPage,
        limit: isGenderFilter ? 1000 : pageSize,
      },
    });
  };

  const fetchEquipmentsTitles = () => {
    getEquipmentstitle({
      callback: (data: EquipmentTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: "Failed",
            description: error,
            status: "error",
          });
          return;
        }
        if (!data) return null;
        setEquipmentsTitle(data);
      },
      command: ApiCommand.GET,
      url: getEquipmentsTitleUrl,
    });
  };

  const fetchResourcesTitles = () => {
    getResourcesTitle({
      callback: (data: ResourceTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: "Failed",
            description: error,
            status: "error",
          });
          return;
        }
        if (!data) return null;
        setResourcesTitle(data);
      },
      command: ApiCommand.GET,
      url: getResourcesTitleUrl,
    });
  };

  useEffect(() => {
    // sendRequest returned from useApi is recreated each render; keep effect deps focused on page size
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchEquipmentsTitles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchResourcesTitles();
  }, [currentPage, pageSize, selectedGenderTab]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleGenderTabChange = (idx: number) => {
    setCurrentPage(1);
    setSelectedGenderTab(idx === 0 ? "all" : idx === 1 ? "male" : "female");
  };

  const onSaveSection = (
    title: string,
    type: string,
    pid: string,
    description: string,
    short_description: string,
    introVimeoId: string,
    purchaseLink: string,
    selectedequipments: string[],
    nutrition: string,
    selectedresources: string[],
    duration: string,
    cover: File | null,
    thumbnail: File | null,
    isFeatured: boolean,
    genders: string[],
  ) => {
      console.log("Submitted modal data:", {
    title,
    type,
    pid,
    short_description,
    selectedequipments,
    nutrition,
    selectedresources,
    duration,
    cover,
    thumbnail,
    isFeatured,
  });

    const formData = new FormData();
    formData.append("title", title);
    formData.append("type", type);
    formData.append("pid", pid);
    formData.append("description", description);
    formData.append("short_description", short_description);
    formData.append("introVimeoId", introVimeoId);
    formData.append("purchaseLink", purchaseLink);
    formData.append("selectedequipments", JSON.stringify(selectedequipments));
    formData.append("nutrition", nutrition);
    formData.append("selectedresources", JSON.stringify(selectedresources));
    formData.append("duration", duration);
    formData.append("isFeatured", String(isFeatured));
    formData.append("genders", JSON.stringify(genders));
    if (thumbnail) {
      formData.append("thumbnail", thumbnail);
    }

    if (cover) {
      formData.append("cover", cover);
    }

    if (selectedSection) {
      formData.append("sectionId", selectedSection?._id || "");
      editSection({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: "Failed",
              description: error,
              status: "error",
            });
            return;
          }
          showToast({
            title: "Success",
            description: "Program Info updated successfully",
            status: "success",
          });
          setIsOpenModal(false);
          fetchSections();
        },
        command: ApiCommand.PUT,
        url: editSectionUrl,
        options: formData,
      });
    } else {
      createSection({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: "Failed",
              description: error,
              status: "error",
            });
            return;
          }
          showToast({
            title: "Success",
            description: "Program Info created successfully",
            status: "success",
          });
          console.log(_data.section._id);
          setIsOpenModal(false);
          onEditSection(_data.section._id);
          // fetchSections();
        },
        command: ApiCommand.POST,
        url: createSectionUrl,
        options: formData,
      });
    }
  };

  const onDeleteSection = (sectionId: string) => {
    console.log(sectionId);
    deleteSection({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: "Failed",
            description: error,
            status: "error",
          });
          return;
        }
        showToast({
          title: "Success",
          description: "Program Info deleted successfully",
          status: "success",
        });
        fetchSections();
      },
      command: ApiCommand.DELETE,
      url: deleteSectionUrl,
      options: {
        sectionId,
      },
    });
  };

  const onDuplicateSection = (sectionId: string) => {
    duplicateSection({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({ title: "Failed", description: error,
   status: "error" });
          return;
        }
        showToast({
          title: "Success",
          description: "Program duplicated successfully",
          status: "success",
        });
        fetchSections();
      },
      command: ApiCommand.POST,
      url: `${duplicateSectionUrl}/${sectionId}`,
    });
  };


  const onEditSection = (sectionId: string) => {
    navigate(Path.PROGRAM_EDIT.replace(":id", sectionId));
    // setSelectedSection(sections.find(({ id }) => id === sectionId));
    // setIsOpenModal(true);
  };

  const filteredSections = filterSectionsByGender(sections, selectedGenderTab);
  const visibleSections =
    selectedGenderTab === "all"
      ? filteredSections
      : filteredSections.slice(
          (currentPage - 1) * pageSize,
          currentPage * pageSize,
        );

  return (
    <Page>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb={6}
        gap={4}
        flexWrap="wrap"
      >
        <Box flex="1" maxW="400px">
          <StrongInput
            placeholder="Search"
            rightIcon={<MdSearch color="gray.500" />}
          />
        </Box>
        <Button
          colorScheme="primary"
          px={4}
          py={4}
          borderRadius="md"
          _hover={{ bg: "primary.300" }}
          onClick={() => {
            setSelectedSection(undefined);
            setIsOpenModal(true);
          }}
        >
          Create Program
        </Button>
      </Flex>

      <Flex flexDirection="column" height="full" justifyContent="space-between">
        <Box
          backgroundColor="white"
          borderRadius="lg"
          height="calc(100vh - 275px)"
          overflow="hidden"
        >
          <Tabs
            index={selectedGenderTab === 'all' ? 0 : selectedGenderTab === 'male' ? 1 : 2}
            onChange={handleGenderTabChange}
            display="flex"
            flexDirection="column"
            height="full"
          >
              <TabList>
                <Tab>All</Tab>
                <Tab>Male</Tab>
                <Tab>Female</Tab>
              </TabList>
              <TabPanels flex="1" overflowY="auto">
                <TabPanel p={0}>
                  <StrongTable
                    columns={SECTION_COLUMNS}
                    data={visibleSections}
                  loading={isFetchUsersLoading}
                  actions={(row) => (
                    <>
                      <IconButton
                        aria-label="Duplicate Section"
                        icon={<MdCopyAll />}
                        variant="outline"
                        colorScheme="blue"
                        size="sm"
                        marginRight={4}
                        isLoading={isDuplicateSectionLoading}
                        onClick={() => onDuplicateSection(row._id)}
                      />
                      <IconButton
                        aria-label="Edit Section"
                        icon={<MdEdit />}
                        variant="outline"
                        colorScheme="primary"
                        size="sm"
                        marginRight={4}
                        onClick={() => onEditSection(row._id)}
                      />
                      <IconButton
                        aria-label="Delete Section"
                        icon={<MdDelete />}
                        variant="outline"
                        colorScheme="red"
                        size="sm"
                        onClick={() => onDeleteSection(row._id)}
                      />
                    </>
                  )}
                />
              </TabPanel>
              <TabPanel p={0}>
                <StrongTable
                  columns={SECTION_COLUMNS}
                  data={visibleSections}
                  loading={isFetchUsersLoading}
                  actions={(row) => (
                    <>
                      <IconButton
                        aria-label="Duplicate Section"
                        icon={<MdCopyAll />}
                        variant="outline"
                        colorScheme="blue"
                        size="sm"
                        marginRight={4}
                        isLoading={isDuplicateSectionLoading}
                        onClick={() => onDuplicateSection(row._id)}
                      />
                      <IconButton
                        aria-label="Edit Section"
                        icon={<MdEdit />}
                        variant="outline"
                        colorScheme="primary"
                        size="sm"
                        marginRight={4}
                        onClick={() => onEditSection(row._id)}
                      />
                      <IconButton
                        aria-label="Delete Section"
                        icon={<MdDelete />}
                        variant="outline"
                        colorScheme="red"
                        size="sm"
                        onClick={() => onDeleteSection(row._id)}
                      />
                    </>
                  )}
                />
              </TabPanel>
              <TabPanel p={0}>
                <StrongTable
                  columns={SECTION_COLUMNS}
                  data={visibleSections}
                  loading={isFetchUsersLoading}
                  actions={(row) => (
                    <>
                      <IconButton
                        aria-label="Duplicate Section"
                        icon={<MdCopyAll />}
                        variant="outline"
                        colorScheme="blue"
                        size="sm"
                        marginRight={4}
                        isLoading={isDuplicateSectionLoading}
                        onClick={() => onDuplicateSection(row._id)}
                      />
                      <IconButton
                        aria-label="Edit Section"
                        icon={<MdEdit />}
                        variant="outline"
                        colorScheme="primary"
                        size="sm"
                        marginRight={4}
                        onClick={() => onEditSection(row._id)}
                      />
                      <IconButton
                        aria-label="Delete Section"
                        icon={<MdDelete />}
                        variant="outline"
                        colorScheme="red"
                        size="sm"
                        onClick={() => onDeleteSection(row._id)}
                      />
                    </>
                  )}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditProgramInfoModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSaveSection}
        selectedSection={selectedSection}
        equipmentstitle={equipmentstitle}
        resourcestitle={resourcestitle}
      />
    </Page>
  );
};

export default ProgramInfo;
